import { pickPreferredPrice } from '../domains/sale/preferred-price.calc.js';
import { composeRateUom } from './currency-area-mapping.js';
import { builtInUomPropagationMap } from './propagation-map.js';
/** Normalise a raw context/changed value to the UOM write domain (undefined → null). */
function norm(value) {
  return value == null ? null : value;
}
function isBlank(value) {
  return value == null || value === '';
}
/**
 * Run one propagation pass. Pure and synchronous — only RETURNS the UOM writes, never
 * mutates `ctx`. `map` defaults to {@link builtInUomPropagationMap}.
 */
export function propagateUom(changedOutputs, ctx, map = builtInUomPropagationMap) {
  const pairs = map.filter(e => e.kind === 'pair');
  const groups = map.filter(e => e.kind === 'group');
  const composes = map.filter(e => e.kind === 'compose');
  const autoSelects = map.filter(e => e.kind === 'auto-select-first');
  /** Every key claimed this pass (writes AND same-value no-ops) — first claim wins. */
  const claims = new Map();
  /** The actual writes, in claim order. */
  const writes = [];
  /** UOM-changed events still to propagate through R3/R4. */
  const queue = [];
  /** Effective current value: claimed-this-pass value, else host state. */
  const eff = key => claims.has(key) ? claims.get(key) : ctx.get(key);
  /** Claim `key := value`; emit + enqueue only when it actually changes the field. */
  const claim = (key, value) => {
    if (claims.has(key)) {
      return; // a higher-priority (or earlier) rule already owns this key
    }
    claims.set(key, value);
    if (!Object.is(norm(ctx.get(key)), value)) {
      writes.push({
        key,
        value
      });
      queue.push({
        key,
        value
      });
    }
  };
  // ── R1 / R2 — value-driven (doc §2 rows R1/R2; bulkedit commonSection.js is canonical) ──
  for (const co of changedOutputs) {
    for (const pair of pairs) {
      if (!pair.values) {
        continue;
      }
      const side = pair.values[0] === co.key ? 0 : pair.values[1] === co.key ? 1 : -1;
      if (side === -1) {
        continue;
      }
      if (co.value == null) {
        // R2: nulling a mapped value clears BOTH UOMs of every pair it belongs to.
        claim(pair.a, null);
        claim(pair.b, null);
      } else if (co.origin === 'rule') {
        // R1: a conversion wrote this side → its UOM := the driving side's current UOM.
        const targetUom = side === 0 ? pair.a : pair.b;
        const sourceUom = side === 0 ? pair.b : pair.a;
        claim(targetUom, norm(eff(sourceUom)));
      }
    }
    // R4 (value half): a mapped value change re-asserts its owner's UOM on the group.
    // Doc §2 R4: nulls ignored — a blank owner UOM re-asserts nothing (groups are only
    // cleared via R2), unlike the raw F4 listener which would sync the group to null.
    if (co.value != null) {
      for (const group of groups) {
        const owner = group.valueOwners?.[co.key];
        if (owner == null) {
          continue;
        }
        const ownerUom = norm(eff(owner));
        if (ownerUom == null) {
          continue;
        }
        for (const member of group.members) {
          claim(member, ownerUom);
        }
      }
    }
  }
  // ── R3 / R4 — uom-driven. Seed with the incoming UOM edits (any origin); phase-1
  //    claims are already queued. Drain fully so same-pass writes cascade (FE-U4). ──
  for (const co of changedOutputs) {
    const key = co.key;
    const isMappedUom = pairs.some(p => p.a === key || p.b === key) || groups.some(g => g.members.includes(key));
    if (isMappedUom) {
      queue.push({
        key,
        value: norm(co.value)
      });
    }
  }
  const drain = () => {
    while (queue.length > 0) {
      const evt = queue.shift();
      // R3 — pair mirror (before R4: rule priority within the uom-driven tier).
      for (const pair of pairs) {
        const partner = pair.a === evt.key ? pair.b : pair.b === evt.key ? pair.a : null;
        if (partner == null) {
          continue;
        }
        if (evt.value == null && pair.onNull === 'ignore') {
          continue;
        }
        if (pair.mirrorRequiresBothValues) {
          // F5 guard, kept verbatim (Q2): truthy UOM + both paired values non-null.
          if (!evt.value || pair.values == null) {
            continue;
          }
          if (ctx.get(pair.values[0]) == null || ctx.get(pair.values[1]) == null) {
            continue;
          }
        }
        claim(partner, evt.value);
      }
      // R4 — group sync; nulls ignored (FE-U7).
      if (evt.value != null) {
        for (const group of groups) {
          if (!group.members.includes(evt.key)) {
            continue;
          }
          for (const member of group.members) {
            if (member !== evt.key) {
              claim(member, evt.value);
            }
          }
        }
      }
    }
  };
  drain();
  // ── R5 — compose (rule-written triggers only: the legacy writes happen inside the
  //    calc that produced the value, never on a hand-edit of the result field). ──
  for (const compose of composes) {
    const co = changedOutputs.find(o => o.key === compose.trigger && o.origin === 'rule');
    if (co == null) {
      continue;
    }
    if (co.value == null) {
      if (compose.onTriggerNull === 'clear') {
        claim(compose.out, null);
      }
      continue;
    }
    if (compose.preferredOf) {
      // SA4: the UOM of whichever candidate pickPreferredPrice selects (candidate order
      // is the preference order). None qualifying → the legacy calc could not have run.
      const winner = compose.preferredOf.find(c => pickPreferredPrice(ctx.get(c.value), null) != null);
      if (winner) {
        claim(compose.out, norm(eff(winner.uom)));
      }
    } else if (compose.copyOf) {
      // SA7: verbatim copy — including null (legacy patches NRAUOMFC.value unchecked).
      claim(compose.out, norm(eff(compose.copyOf)));
    } else if (compose.currencyUom) {
      const currency = norm(eff(compose.currencyUom));
      const area = compose.areaLiteral ?? (compose.areaUom ? norm(eff(compose.areaUom)) : null);
      if (currency == null || area == null) {
        continue;
      }
      const composed = composeRateUom(currency, area);
      if (composed != null) {
        claim(compose.out, composed);
      } else if (compose.sqmFallback) {
        claim(compose.out, `${currency}/SqM`); // SA6 legacy fallback (L835–836)
      }
      // else: off-table lookup → no write (legacy would patch `undefined`; the rule
      // gates make this unreachable on the legacy path — reconciled to a clean skip).
    }
  }
  drain(); // compose writes may feed pairs/groups in custom maps
  // ── R6 / R7 — auto-select-first + country default, over every mapped value→UOM
  //    relation whose VALUE changed non-null this pass (F6 fires on value edits). ──
  const relations = [];
  for (const pair of pairs) {
    if (pair.values) {
      relations.push({
        value: pair.values[0],
        uom: pair.a
      }, {
        value: pair.values[1],
        uom: pair.b
      });
    }
  }
  for (const group of groups) {
    for (const [value, uom] of Object.entries(group.valueOwners ?? {})) {
      relations.push({
        value: value,
        uom: uom
      });
    }
  }
  for (const auto of autoSelects) {
    relations.push({
      value: auto.value,
      uom: auto.uom
    });
  }
  const changedNonNullValues = new Set(changedOutputs.filter(o => o.value != null).map(o => o.key));
  const eligible = relations.filter(r => changedNonNullValues.has(r.value));
  /**
   * R6/R7 fill a key whose EFFECTIVE value is "still blank" after R1–R5 (doc §2 R6) — a
   * blank claimed by an earlier rule does not block the fill, but the result still carries
   * at most one entry per key (the earlier blank write is replaced, or dropped when the
   * fill restores the field's original value). Fills are terminal: they never re-enter
   * R3/R4 (legacy F6 fills exactly one control).
   */
  const fillBlank = (key, value) => {
    if (!isBlank(eff(key))) {
      return;
    }
    claims.set(key, value);
    const existing = writes.find(w => w.key === key);
    const isOriginalValue = Object.is(norm(ctx.get(key)), value);
    if (existing) {
      if (isOriginalValue) {
        writes.splice(writes.indexOf(existing), 1);
      } else {
        existing.value = value;
      }
    } else if (!isOriginalValue) {
      writes.push({
        key,
        value
      });
    }
  };
  if (ctx.optionsFor) {
    for (const r of eligible) {
      // Legacy F6 takes `values[0].key ?? .value`; the port returns plain strings.
      const first = ctx.optionsFor(r.uom)?.[0];
      if (first) {
        fillBlank(r.uom, first);
      }
    }
  }
  if (ctx.defaultUomFor) {
    for (const r of eligible) {
      const fallback = ctx.defaultUomFor(r.uom);
      if (fallback) {
        fillBlank(r.uom, fallback);
      }
    }
  }
  return writes;
}
